import React, { useState, useEffect } from 'react';
import API_BASE from '../config';
import { FiEdit, FiTrash2, FiPlus, FiX, FiCheckCircle, FiSave, FiEye } from 'react-icons/fi';

export default function BlogManager() {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingBlog, setEditingBlog] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [imageFile, setImageFile] = useState(null);

    const fetchBlogs = () => {
        fetch(`${API_BASE}/admin/blogs.php`)
            .then(r => r.json())
            .then(data => {
                if (data.status === 'success') setBlogs(data.posts || []);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchBlogs();
    }, []);

    const handleEdit = (blog) => {
        fetch(`${API_BASE}/admin/blogs.php?id=${blog.id}`)
            .then(r => r.json())
            .then(data => {
                if (data.status === 'success') {
                    setEditingBlog(data.post);
                    setImageFile(null); // Reset file input when editing
                    setShowModal(true);
                }
            });
    };

    const handleDelete = (id) => {
        if (!window.confirm('Delete this blog post?')) return;
        fetch(`${API_BASE}/admin/blogs.php?id=${id}`, { method: 'DELETE' })
            .then(r => r.json())
            .then(() => fetchBlogs());
    };

    const handleSave = (e) => {
        e.preventDefault();

        const formData = new FormData();
        Object.keys(editingBlog).forEach(key => {
            formData.append(key, editingBlog[key] || '');
        });

        if (imageFile) {
            formData.append('image_file', imageFile);
        }

        fetch(`${API_BASE}/admin/blogs.php`, {
            method: 'POST',
            body: formData
        })
            .then(r => r.json())
            .then(() => {
                setShowModal(false);
                setImageFile(null);
                fetchBlogs();
            });
    };

    if (loading) return <div className="p-12 text-center text-slate-400 italic">Reading blog archive...</div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-slate-500 text-sm font-bold uppercase tracking-widest">Active Publications</h3>
                <button
                    onClick={() => {
                        setEditingBlog({ title: '', slug: '', excerpt: '', content: '', author: 'Admin', category: 'Design', is_published: 1, published_at: new Date().toISOString().split('T')[0] });
                        setImageFile(null);
                        setShowModal(true);
                    }}
                    className="btn-yellow flex items-center gap-2 px-6 py-2.5 text-sm"
                >
                    <FiPlus /> New Post
                </button>
            </div>

            <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-slate-50 border-b">
                        <tr>
                            <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Blog Post</th>
                            <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Metadata</th>
                            <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Status</th>
                            <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y text-sm">
                        {blogs.map((b) => (
                            <tr key={b.id} className="hover:bg-slate-50/80 transition-colors">
                                <td className="px-6 py-5">
                                    <div className="font-bold text-slate-900 text-base">{b.title}</div>
                                    <div className="text-slate-400 text-xs mt-1 font-mono">/{b.slug}</div>
                                </td>
                                <td className="px-6 py-5">
                                    <div className="text-slate-700 font-semibold">{b.category}</div>
                                    <div className="text-slate-400 text-xs mt-1">By {b.author}</div>
                                </td>
                                <td className="px-6 py-5">
                                    {b.is_published ? (
                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold uppercase border border-emerald-100">
                                            <FiCheckCircle size={10} /> Published
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold uppercase border border-slate-200">
                                            Draft
                                        </span>
                                    )}
                                </td>
                                <td className="px-6 py-5 text-right">
                                    <div className="inline-flex items-center gap-2">
                                        <a href={`/blog/${b.slug}`} target="_blank" className="p-2 text-slate-400 hover:text-[#2E63E8] transition-colors"><FiEye size={18} /></a>
                                        <button onClick={() => handleEdit(b)} className="p-2 text-slate-400 hover:text-[#2E63E8] transition-colors"><FiEdit size={18} /></button>
                                        <button onClick={() => handleDelete(b.id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors"><FiTrash2 size={18} /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Editor Modal */}
            {showModal && (
                <div className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl animate-in zoom-in-95 duration-200">
                        <div className="p-8 border-b sticky top-0 bg-white z-10 flex items-center justify-between">
                            <h2 className="text-2xl font-bold text-slate-800">{editingBlog?.id ? 'Edit' : 'Create'} Publication</h2>
                            <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors font-bold"><FiX size={24} /></button>
                        </div>

                        <form onSubmit={handleSave} className="p-8 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1">
                                    <label className="text-sm font-bold text-slate-500 tracking-tight uppercase">Article Title</label>
                                    <input
                                        type="text"
                                        required
                                        value={editingBlog.title}
                                        onChange={e => setEditingBlog({ ...editingBlog, title: e.target.value })}
                                        className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-[#2E63E8] focus:border-transparent outline-none transition-all"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-bold text-slate-500 tracking-tight uppercase">URL Slug</label>
                                    <input
                                        type="text"
                                        placeholder="Auto-generated if empty"
                                        value={editingBlog.slug}
                                        onChange={e => setEditingBlog({ ...editingBlog, slug: e.target.value })}
                                        className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-[#2E63E8] focus:border-transparent outline-none transition-all"
                                    />
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm font-bold text-slate-500 tracking-tight uppercase">Excerpt (Summary)</label>
                                <textarea
                                    rows="3"
                                    value={editingBlog.excerpt}
                                    onChange={e => setEditingBlog({ ...editingBlog, excerpt: e.target.value })}
                                    className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-[#2E63E8] focus:border-transparent outline-none transition-all resize-none"
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm font-bold text-slate-500 tracking-tight uppercase">Full Content (HTML allowed)</label>
                                <textarea
                                    rows="10"
                                    required
                                    value={editingBlog.content}
                                    onChange={e => setEditingBlog({ ...editingBlog, content: e.target.value })}
                                    className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-[#2E63E8] focus:border-transparent outline-none font-mono text-sm transition-all"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t">
                                <div>
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest pl-1">Author</label>
                                    <input type="text" value={editingBlog.author} onChange={e => setEditingBlog({ ...editingBlog, author: e.target.value })} className="w-full border p-2.5 rounded-xl mt-1 text-sm outline-none" />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest pl-1">Category</label>
                                    <input type="text" value={editingBlog.category} onChange={e => setEditingBlog({ ...editingBlog, category: e.target.value })} className="w-full border p-2.5 rounded-xl mt-1 text-sm outline-none" />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest pl-1">Featured Image</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={e => setImageFile(e.target.files[0])}
                                        className="w-full border p-2 rounded-xl mt-1 text-sm outline-none file:mr-2 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-[10px] file:font-semibold file:bg-[#2E63E8]/10 file:text-[#b89511] hover:file:bg-[#2E63E8]/20"
                                    />
                                    {editingBlog.featured_image && !imageFile && (
                                        <p className="text-[10px] text-slate-400 mt-1 pl-1 line-clamp-1">Current: {editingBlog.featured_image.split('/').pop()}</p>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-8">
                                <label className="flex items-center gap-3 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={editingBlog.is_published}
                                        onChange={e => setEditingBlog({ ...editingBlog, is_published: e.target.checked ? 1 : 0 })}
                                        className="w-5 h-5 rounded border-slate-300 text-[#2E63E8] focus:ring-[#2E63E8]"
                                    />
                                    <span className="text-sm font-bold text-slate-600 group-hover:text-slate-900 transition-colors">Publish immediately</span>
                                </label>
                                <button type="submit" className="btn-yellow px-10 py-3.5 flex items-center gap-2">
                                    <FiSave /> Save Article
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
